<template>
  <p v-if="isLoading" class="text-subtle">Hämtar autogiro...</p>
  <p v-else-if="isNotFound" class="text-subtle">Ingen information.</p>
  <p v-else-if="hasError" class="error-text">Kunde inte hämta autogiro just nu. Försök igen senare.</p>
  <p v-else-if="!account && !mandateAwaitingSignature" class="text-subtle">Inget autogiro registrerat.</p>

  <div v-else class="autogiro-sections">
    <div class="planned-debit-account">
      <h3>{{ fieldLabel(RESOURCE, 'plannedDebitAccount') }}</h3>
      <p v-if="!account" class="text-subtle">Inget autogiro registrerat.</p>
      <dl v-else class="autogiro">
        <div class="autogiro-field">
          <dt class="text-subtle">{{ fieldLabel(RESOURCE, 'plannedDebitAccount.accountNumber') }}</dt>
          <dd class="account-number">
            {{ account.accountNumber }}
            <AppButton icon small aria-label="Kopiera kontonummer" @click="copyAccountNumber(account.accountNumber)">
              <SvgIcon :path="mdiContentCopy" :size="16" />
            </AppButton>
          </dd>
        </div>
        <div class="autogiro-field">
          <dt class="text-subtle">{{ fieldLabel(RESOURCE, 'plannedDebitAccount.mandateStatus') }}</dt>
          <dd class="mandate-status">
            <AppChip :color="mandateStatusChipColor(account.mandateStatus)" dot>{{ mandateStatusLabel }}</AppChip>
            <p v-if="mandateStatusHelpText" class="mandate-help-text text-subtle">{{ mandateStatusHelpText }}</p>
          </dd>
        </div>
      </dl>
    </div>

    <div v-if="mandateAwaitingSignature" class="mandate-awaiting-signature">
      <h3>{{ fieldLabel(RESOURCE, 'mandateAwaitingSignature') }}</h3>
      <dl class="autogiro">
        <div class="autogiro-field">
          <dt class="text-subtle">{{ fieldLabel(RESOURCE, 'mandateAwaitingSignature.accountNumber') }}</dt>
          <dd class="account-number">
            {{ mandateAwaitingSignature.accountNumber }}
            <AppButton
              icon
              small
              aria-label="Kopiera kontonummer"
              @click="copyAccountNumber(mandateAwaitingSignature.accountNumber)"
            >
              <SvgIcon :path="mdiContentCopy" :size="16" />
            </AppButton>
          </dd>
        </div>
        <div class="autogiro-field">
          <dt class="text-subtle">{{ fieldLabel(RESOURCE, 'mandateAwaitingSignature.signingLink') }}</dt>
          <dd class="account-number">
            <a
              :href="mandateAwaitingSignature.signingLink"
              target="_blank"
              rel="noopener noreferrer"
              class="scrive-link font-medium"
            >
              Öppna
            </a>
            <AppButton
              icon
              small
              aria-label="Kopiera signeringslänk"
              @click="copySigningLink(mandateAwaitingSignature.signingLink)"
            >
              <SvgIcon :path="mdiContentCopy" :size="16" />
            </AppButton>
          </dd>
        </div>
      </dl>
      <p class="text-subtle">Kunden behöver signera medgivandet för att kontot ska bli nytt autogirokonto.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { AppButton, AppChip, SvgIcon, notify, type ChipColor } from '@staff/ui'
import { mdiContentCopy } from '@mdi/js'

import { useCustomerAutogiro } from '@/composables/useCustomerAutogiro'
import { useMetadata } from '@/composables/useMetadata'
import type { AutogiroMandateStatus } from '@/services/autogiro'

const RESOURCE = 'customerAutogiro'

const props = defineProps<{
  userId: string
}>()

const { metadata, fieldLabel, getReferenceValues } = useMetadata()

const { isLoading, isNotFound, hasError, account, mandateAwaitingSignature } = useCustomerAutogiro(
  toRef(props, 'userId'),
)

const copyAccountNumber = async (accountNumber: string) => {
  try {
    await navigator.clipboard.writeText(accountNumber)
    notify({ type: 'success', title: 'Kontonummer kopierat' })
  } catch (err) {
    console.error('Failed to copy account number', err)
    notify({ type: 'error', title: 'Kunde inte kopiera kontonumret', dismissable: true })
  }
}

const copySigningLink = async (signingLink: string) => {
  try {
    await navigator.clipboard.writeText(signingLink)
    notify({ type: 'success', title: 'Signeringslänk kopierad' })
  } catch (err) {
    console.error('Failed to copy signing link', err)
    notify({ type: 'error', title: 'Kunde inte kopiera signeringslänken', dismissable: true })
  }
}

const mandateStatusReference = computed(
  () =>
    metadata.value?.resources[RESOURCE]?.fields['plannedDebitAccount.mandateStatus']?.reference ??
    'autogiroMandateStatuses',
)

const mandateStatusValue = computed(() =>
  getReferenceValues(mandateStatusReference.value).find((item) => item.code === account.value?.mandateStatus),
)

const mandateStatusLabel = computed(() =>
  account.value?.mandateStatus ? (mandateStatusValue.value?.label ?? account.value.mandateStatus) : 'Okänd status',
)

// An approved mandate needs no explanation beyond the chip.
const mandateStatusHelpText = computed(() =>
  account.value?.mandateStatus === 'approved' ? undefined : mandateStatusValue.value?.helpText,
)

const mandateStatusChipColor = (status: AutogiroMandateStatus | null): ChipColor => {
  switch (status) {
    case 'approved':
      return 'success'
    case 'pending':
      return 'warning'
    case 'failed':
    case 'removed':
      return 'danger'
    default:
      return 'muted'
  }
}
</script>

<style scoped lang="scss">
.autogiro {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: var(--space-40);
  margin: 0;
}

.autogiro-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

dd {
  margin: 0;
}

.account-number {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.mandate-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-6);
}

.mandate-help-text {
  white-space: pre-line;
}

.autogiro-sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
}

.planned-debit-account,
.mandate-awaiting-signature {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.scrive-link {
  color: var(--color-brand-default);

  &:hover {
    color: var(--color-brand-emphasis);
    text-decoration: underline;
  }
}
</style>
